import type { jsPDF } from 'jspdf'
import { PDF_COLORS, PDF_FONT, PDF_LAYOUT, type RGB } from './theme'
import type { ReportBrand } from './branding'

export type PageContext = {
  pageWidth: number
  pageHeight: number
  contentLeft: number
  contentRight: number
  contentWidth: number
}

export function pageContext(doc: jsPDF): PageContext {
  const pageWidth = doc.internal.pageSize.getWidth()
  const pageHeight = doc.internal.pageSize.getHeight()
  const contentLeft = PDF_LAYOUT.margin
  const contentRight = pageWidth - PDF_LAYOUT.margin
  return {
    pageWidth,
    pageHeight,
    contentLeft,
    contentRight,
    contentWidth: contentRight - contentLeft,
  }
}

function setFill(doc: jsPDF, c: RGB) {
  doc.setFillColor(c[0], c[1], c[2])
}
function setText(doc: jsPDF, c: RGB) {
  doc.setTextColor(c[0], c[1], c[2])
}
function setDraw(doc: jsPDF, c: RGB) {
  doc.setDrawColor(c[0], c[1], c[2])
}

/**
 * Branded header drawn at the top of every page. Returns the Y position
 * where downstream content should start.
 *
 *  ┌────────────────────────────────────────────────────────────────────┐
 *  │ ▌ BUSINESS NAME                                Report Title         │
 *  │ ▌ business email · phone                       Report subtitle      │
 *  └────────────────────────────────────────────────────────────────────┘
 *
 * Uses a subtle accent stripe on the left so the header reads as a brand
 * mark even when the tenant has no logo configured.
 */
export function drawBrandHeader(
  doc: jsPDF,
  brand: ReportBrand,
  title: string,
  subtitle?: string,
): number {
  const { contentLeft, contentRight, contentWidth } = pageContext(doc)
  const top = PDF_LAYOUT.topMargin
  const stripeW = 1.4

  // Accent stripe on the left
  setFill(doc, PDF_COLORS.brandPrimary)
  doc.rect(contentLeft, top, stripeW, PDF_LAYOUT.headerHeight, 'F')

  // Business name
  doc.setFont('helvetica', 'bold')
  doc.setFontSize(PDF_FONT.companyName)
  setText(doc, PDF_COLORS.ink)
  doc.text(brand.businessName.toUpperCase(), contentLeft + stripeW + 3, top + 6)

  // Contact line under business name
  doc.setFont('helvetica', 'normal')
  doc.setFontSize(PDF_FONT.metaLabel)
  setText(doc, PDF_COLORS.muted)
  const contact: string[] = []
  if (brand.email) contact.push(brand.email)
  if (brand.phone) contact.push(brand.phone)
  if (brand.addressLines[0]) contact.push(brand.addressLines[0])
  if (contact.length) {
    doc.text(contact.join('  ·  '), contentLeft + stripeW + 3, top + 11)
  }

  // Right-aligned report title + subtitle
  doc.setFont('helvetica', 'bold')
  doc.setFontSize(PDF_FONT.reportTitle)
  setText(doc, PDF_COLORS.ink)
  doc.text(title, contentRight, top + 8, { align: 'right' })

  if (subtitle) {
    doc.setFont('helvetica', 'normal')
    doc.setFontSize(PDF_FONT.metaValue)
    setText(doc, PDF_COLORS.muted)
    doc.text(subtitle, contentRight, top + 14, { align: 'right' })
  }

  // Hairline under the header
  setDraw(doc, PDF_COLORS.hairline)
  doc.setLineWidth(PDF_LAYOUT.mediumLine)
  const hairY = top + PDF_LAYOUT.headerHeight - 6
  doc.line(contentLeft, hairY, contentRight, hairY)

  // Reset state
  doc.setFont('helvetica', 'normal')
  setText(doc, PDF_COLORS.body)
  setDraw(doc, PDF_COLORS.hairline)

  void contentWidth // (intentionally unused but documents intent)
  // Why: the +6 buffer keeps autoTable column headers and any first-row
  // content from kissing the stripe bottom on page 2+; the previous +2
  // was tight enough that long titles + descender-heavy fonts could
  // touch the row above.
  return top + PDF_LAYOUT.headerHeight + 6
}

/**
 * Two-column metadata strip directly under the header. Each entry is a
 * stacked pair (small-caps label above bold value) so long labels never
 * collide with long values - the crash that happened with the previous
 * inline layout when "FISCAL YEAR START" + "April" tried to share a row.
 *
 * Accepts either a single ordered list (auto-balanced 50/50 with the
 * extra row on the left when odd) or pre-split left/right arrays for
 * callers that need full control.
 */
export function drawMetadataStrip(
  doc: jsPDF,
  startY: number,
  entriesOrLeft: Array<[string, string]>,
  pairsRight?: Array<[string, string]>,
): number {
  const { contentLeft, contentRight, contentWidth } = pageContext(doc)

  let pairsLeft: Array<[string, string]>
  let pairsRightFinal: Array<[string, string]>
  if (pairsRight === undefined) {
    // Single-list mode: split with extra rows on the left so a 5-entry
    // list becomes 3 left / 2 right rather than leaving a lonely row
    // on the right.
    const total = entriesOrLeft.length
    const leftCount = Math.ceil(total / 2)
    pairsLeft = entriesOrLeft.slice(0, leftCount)
    pairsRightFinal = entriesOrLeft.slice(leftCount)
  } else {
    pairsLeft = entriesOrLeft
    pairsRightFinal = pairsRight
  }

  const rows = Math.max(pairsLeft.length, pairsRightFinal.length)
  const padding = 4
  const labelH = 3.4
  const valueH = 4.2
  const gap = 2.6
  const entryH = labelH + valueH + gap
  const blockH = padding * 2 + rows * entryH - gap

  setFill(doc, PDF_COLORS.panel)
  doc.setDrawColor(PDF_COLORS.hairline[0], PDF_COLORS.hairline[1], PDF_COLORS.hairline[2])
  doc.setLineWidth(PDF_LAYOUT.thinLine)
  doc.roundedRect(contentLeft, startY, contentWidth, blockH, 1.2, 1.2, 'FD')

  const colW = contentWidth / 2
  const xLeftLabel = contentLeft + padding
  const xRight = contentRight - padding
  const colMid = contentLeft + colW + padding

  const renderColumn = (
    pairs: Array<[string, string]>,
    column: 'left' | 'right',
  ) => {
    let y = startY + padding + labelH
    for (const [label, value] of pairs) {
      // Label (small caps, muted)
      doc.setFont('helvetica', 'normal')
      doc.setFontSize(PDF_FONT.metaLabel)
      setText(doc, PDF_COLORS.muted)
      if (column === 'left') {
        doc.text(label.toUpperCase(), xLeftLabel, y)
      } else {
        doc.text(label.toUpperCase(), colMid, y)
      }

      // Value (bold, dark) - one line below the label
      doc.setFont('helvetica', 'bold')
      doc.setFontSize(PDF_FONT.metaValue)
      setText(doc, PDF_COLORS.ink)
      const yValue = y + valueH
      const maxValueW = colW - padding * 2
      const valueText = truncateToWidth(doc, value, maxValueW)
      if (column === 'left') {
        doc.text(valueText, xLeftLabel, yValue)
      } else {
        // Right column also left-aligned at colMid for readability;
        // a single right-aligned value would force a fixed gap and
        // truncate the label text.
        doc.text(valueText, colMid, yValue)
      }
      y += entryH
    }
    void xRight // kept for potential future right-aligned variant
  }

  renderColumn(pairsLeft, 'left')
  renderColumn(pairsRightFinal, 'right')

  // Reset
  doc.setFont('helvetica', 'normal')
  setText(doc, PDF_COLORS.body)
  return startY + blockH + 4
}

/** Truncate a string with an ellipsis until it fits the given width (mm). */
function truncateToWidth(doc: jsPDF, text: string, maxWidth: number): string {
  if (doc.getTextWidth(text) <= maxWidth) return text
  let s = text
  while (s.length > 1 && doc.getTextWidth(s + '…') > maxWidth) {
    s = s.slice(0, -1)
  }
  return s + '…'
}

/**
 * Full-width section bar (e.g. "Assets", "Cash flows from operating
 * activities"). Use for top-level groupings. Matches the in-app reporting
 * tables: light grey panel + dark bold text - subtle visual hierarchy
 * that doesn't compete with the column header strip above.
 */
export function drawSectionBar(
  doc: jsPDF,
  y: number,
  label: string,
  rightHint?: string,
): number {
  const { contentLeft, contentRight, contentWidth } = pageContext(doc)
  const h = 7
  setFill(doc, PDF_COLORS.panelStrong)
  doc.rect(contentLeft, y, contentWidth, h, 'F')
  doc.setFont('helvetica', 'bold')
  doc.setFontSize(PDF_FONT.sectionHeader)
  setText(doc, PDF_COLORS.ink)
  doc.text(label, contentLeft + 4, y + 4.9)
  if (rightHint) {
    doc.setFont('helvetica', 'normal')
    doc.setFontSize(PDF_FONT.metaLabel)
    setText(doc, PDF_COLORS.muted)
    doc.text(rightHint, contentRight - 4, y + 4.9, { align: 'right' })
  }
  setText(doc, PDF_COLORS.body)
  return y + h + 0.4
}

/** Light background bar for a sub-grouping (e.g. category within a section). */
export function drawSubsectionBar(
  doc: jsPDF,
  y: number,
  label: string,
  indent = 1,
): number {
  const { contentLeft, contentWidth } = pageContext(doc)
  const h = 5.6
  setFill(doc, PDF_COLORS.panel)
  doc.rect(contentLeft, y, contentWidth, h, 'F')
  doc.setFont('helvetica', 'bold')
  doc.setFontSize(PDF_FONT.subSection)
  setText(doc, PDF_COLORS.ink)
  doc.text(label, contentLeft + 3 + indent * PDF_LAYOUT.indent, y + 3.9)
  setText(doc, PDF_COLORS.body)
  return y + h + 0.4
}

/** Body row: indented label on the left, right-aligned value(s). */
export function drawDataRow(
  doc: jsPDF,
  y: number,
  label: string,
  values: string[],
  opts: {
    indent?: number
    bold?: boolean
    striped?: boolean
    valueColors?: Array<RGB | undefined>
    valueColumnWidth?: number
  } = {},
): number {
  const { contentLeft, contentRight, contentWidth } = pageContext(doc)
  const indent = opts.indent ?? 1
  const rowH = PDF_LAYOUT.rowHeight

  if (opts.striped) {
    setFill(doc, PDF_COLORS.panel)
    doc.rect(contentLeft, y, contentWidth, rowH, 'F')
  }

  doc.setFont('helvetica', opts.bold ? 'bold' : 'normal')
  doc.setFontSize(PDF_FONT.body)
  setText(doc, PDF_COLORS.body)
  doc.text(label, contentLeft + 3 + indent * PDF_LAYOUT.indent, y + 4.4)

  // Render values right-to-left so the rightmost column is always the
  // primary one and any extra (variance) columns sit to its left.
  const colW = opts.valueColumnWidth ?? 38
  let xRight = contentRight - 3
  for (let i = values.length - 1; i >= 0; i--) {
    const c = opts.valueColors?.[i]
    if (c) setText(doc, c)
    else setText(doc, PDF_COLORS.body)
    doc.text(values[i], xRight, y + 4.4, { align: 'right' })
    xRight -= colW
  }

  // Hairline beneath the row
  doc.setDrawColor(PDF_COLORS.hairline[0], PDF_COLORS.hairline[1], PDF_COLORS.hairline[2])
  doc.setLineWidth(PDF_LAYOUT.thinLine)
  doc.line(contentLeft, y + rowH, contentRight, y + rowH)

  setText(doc, PDF_COLORS.body)
  return y + rowH
}

/** Subtotal row: bold, light brand fill, top hairline. */
export function drawSubtotalRow(
  doc: jsPDF,
  y: number,
  label: string,
  values: string[],
  opts: { indent?: number; valueColumnWidth?: number } = {},
): number {
  const { contentLeft, contentRight, contentWidth } = pageContext(doc)
  const rowH = PDF_LAYOUT.totalsBarHeight
  setFill(doc, PDF_COLORS.panelStrong)
  doc.rect(contentLeft, y, contentWidth, rowH, 'F')
  doc.setDrawColor(PDF_COLORS.hairline[0], PDF_COLORS.hairline[1], PDF_COLORS.hairline[2])
  doc.setLineWidth(PDF_LAYOUT.mediumLine)
  doc.line(contentLeft, y, contentRight, y)
  doc.setFont('helvetica', 'bold')
  doc.setFontSize(PDF_FONT.body)
  setText(doc, PDF_COLORS.ink)
  doc.text(label, contentLeft + 3 + (opts.indent ?? 1) * PDF_LAYOUT.indent, y + 5.4)
  const colW = opts.valueColumnWidth ?? 38
  let xRight = contentRight - 3
  for (let i = values.length - 1; i >= 0; i--) {
    doc.text(values[i], xRight, y + 5.4, { align: 'right' })
    xRight -= colW
  }
  return y + rowH + 0.4
}

/**
 * Grand total row. Uses the same light grey band as the section bar so
 * the report reads as one unified palette - the band is differentiated
 * by bold text + a thin top rule rather than a heavier fill.
 */
export function drawGrandTotalRow(
  doc: jsPDF,
  y: number,
  label: string,
  values: string[],
  opts: { valueColumnWidth?: number } = {},
): number {
  const { contentLeft, contentRight, contentWidth } = pageContext(doc)
  const rowH = 7

  setFill(doc, PDF_COLORS.panelStrong)
  doc.rect(contentLeft, y, contentWidth, rowH, 'F')

  // Thin top rule (subtle - matches the in-app totals row separator)
  doc.setDrawColor(PDF_COLORS.muted[0], PDF_COLORS.muted[1], PDF_COLORS.muted[2])
  doc.setLineWidth(PDF_LAYOUT.thinLine)
  doc.line(contentLeft, y, contentRight, y)

  doc.setFont('helvetica', 'bold')
  doc.setFontSize(PDF_FONT.totalLabel)
  setText(doc, PDF_COLORS.ink)
  doc.text(label, contentLeft + 3, y + 4.9)

  const colW = opts.valueColumnWidth ?? 38
  let xRight = contentRight - 3
  for (let i = values.length - 1; i >= 0; i--) {
    doc.text(values[i], xRight, y + 4.9, { align: 'right' })
    xRight -= colW
  }
  setText(doc, PDF_COLORS.body)
  return y + rowH + 0.6
}

/** Coloured banner for a status line (Balanced / OUT OF BALANCE / etc.). */
export function drawStatusBanner(
  doc: jsPDF,
  y: number,
  ok: boolean,
  text: string,
): number {
  const { contentLeft, contentWidth } = pageContext(doc)
  const bg = ok ? PDF_COLORS.successPanel : PDF_COLORS.dangerPanel
  const fg = ok ? PDF_COLORS.success : PDF_COLORS.danger
  setFill(doc, bg)
  doc.roundedRect(contentLeft, y, contentWidth, 8, 1.4, 1.4, 'F')
  doc.setFont('helvetica', 'bold')
  doc.setFontSize(PDF_FONT.body)
  setText(doc, fg)
  doc.text(text, contentLeft + 4, y + 5.5)
  setText(doc, PDF_COLORS.body)
  return y + 8 + 2
}

/**
 * Column header strip drawn just below a section bar. Brand-blue band with
 * white bold text - matches the AutoTable head used by the Trial Balance
 * report so all four reports share the same column-header treatment.
 */
export function drawColumnHeader(
  doc: jsPDF,
  y: number,
  leftLabel: string,
  rightLabels: string[],
  opts: { valueColumnWidth?: number } = {},
): number {
  const { contentLeft, contentRight, contentWidth } = pageContext(doc)
  const h = 6.4
  setFill(doc, PDF_COLORS.brandPrimary)
  doc.rect(contentLeft, y, contentWidth, h, 'F')
  doc.setFont('helvetica', 'bold')
  doc.setFontSize(PDF_FONT.metaLabel)
  setText(doc, PDF_COLORS.white)
  doc.text(leftLabel.toUpperCase(), contentLeft + 3, y + 4.3)
  const colW = opts.valueColumnWidth ?? 38
  let xRight = contentRight - 3
  for (let i = rightLabels.length - 1; i >= 0; i--) {
    doc.text(rightLabels[i].toUpperCase(), xRight, y + 4.3, { align: 'right' })
    xRight -= colW
  }
  setText(doc, PDF_COLORS.body)
  return y + h + 0.4
}

/**
 * Thin horizontal divider used between sub-blocks.
 */
export function drawDivider(doc: jsPDF, y: number, color: RGB = PDF_COLORS.hairline): number {
  const { contentLeft, contentRight } = pageContext(doc)
  setDraw(doc, color)
  doc.setLineWidth(PDF_LAYOUT.thinLine)
  doc.line(contentLeft, y, contentRight, y)
  return y + 1.2
}

/** Add a branded footer to every page in the document at the very end. */
export function paintBrandedFooters(doc: jsPDF, brand: ReportBrand, reportTitle: string) {
  const total = doc.getNumberOfPages()
  for (let i = 1; i <= total; i++) {
    doc.setPage(i)
    const { pageWidth, pageHeight, contentLeft, contentRight } = pageContext(doc)
    const y = pageHeight - 10
    setDraw(doc, PDF_COLORS.hairline)
    doc.setLineWidth(PDF_LAYOUT.thinLine)
    doc.line(contentLeft, y - 4, contentRight, y - 4)
    doc.setFont('helvetica', 'normal')
    doc.setFontSize(PDF_FONT.footer)
    setText(doc, PDF_COLORS.muted)
    const left = `${brand.businessName} · ${reportTitle}`
    doc.text(left, contentLeft, y)
    const right = `Page ${i} of ${total}`
    doc.text(right, contentRight, y, { align: 'right' })
    const center = `Generated ${new Date().toLocaleString(brand.locale)} by Bokapsys`
    doc.text(center, pageWidth / 2, y, { align: 'center' })
  }
  setText(doc, PDF_COLORS.body)
}

/** Page-break helper. Returns a fresh Y if the cursor would overflow. */
export function ensureSpace(doc: jsPDF, y: number, needed: number, brand: ReportBrand, title: string, subtitle?: string): number {
  const { pageHeight } = pageContext(doc)
  if (y + needed < pageHeight - PDF_LAYOUT.bottomMargin) return y
  doc.addPage()
  return drawBrandHeader(doc, brand, title, subtitle)
}
